/**
 * driverProfile.ts — WHICH `profiles` row belongs to the driver.
 *
 * ONE DRIVER CAN HAVE MORE THAN ONE PROFILE ROW, AND ONLY ONE OF THEM IS THE DRIVER'S.
 * `profiles.driver_id` carries a PARTIAL unique index — `UNIQUE (driver_id) WHERE admin_id IS
 * NULL` — precisely so an admin may put their own profile against a driver id for testing. So the
 * embed `drivers → profiles` is not "the driver's profile, wrapped in an array". It is every row
 * pointing at that driver, the driver's own among them, in whatever order PostgREST returns.
 *
 * TAKING `[0]` IS THEREFORE A COIN TOSS, and it has already landed wrong in production: contract
 * 309's driver (272) carries the owner's ADMIN profile (id 226, `admin_id` 1) alongside the real
 * one (id 480), and every surface that unwrapped the embed by position printed the ADMIN — the
 * wrong legal name and a date of birth in the future — on the driver's own setup page, while
 * `get_contract_setup_status`, whose SQL arm has always carried `AND pr.admin_id IS NULL`, refused
 * the activation over the real row's empty fields. One screen, two people.
 *
 * THE EXCLUSION IS THE SERVER'S, RESTATED CLIENT-SIDE — not a second rule. It is applied here,
 * over the rows, rather than as a PostgREST filter on each embed, for two reasons: the app already
 * unwraps every embed in process (so this is the seam that exists), and a mis-spelled embedded
 * filter path is answered by PostgREST with a 400 that takes the whole page down, where a mistake
 * here is caught by the unit test beside this file. WRITES ARE THE EXCEPTION AND MUST FILTER IN
 * THE QUERY: `UPDATE profiles … WHERE driver_id = $1` reaches BOTH rows, and no client-side pick
 * can undo a row that has already been overwritten.
 *
 * PURE, AND REACT-FREE. Nothing here imports the supabase client, so a node-env test can load it.
 */

/**
 * The two columns the pick reads. Every select feeding this helper must ask for `admin_id` — a
 * column PostgREST was not asked for is `undefined`, not null, and the fallback below treats that
 * as "unscoped" so an un-migrated caller is never made WORSE than the `[0]` it replaces.
 */
export interface DriverScopedProfile {
    /** NULL on the driver's own row; the admin's id on a test profile sharing the driver id. */
    admin_id?: number | null;
    /** Only used to break a tie the database's partial unique index already forbids. */
    id?: number | null;
}

/** How PostgREST hands the embed back: as the row, as a list of rows, or not at all. */
export type EmbeddedProfiles<T> = T | T[] | null | undefined;

/**
 * The driver's own profile row, or null when they have none.
 *
 * NULL IS AN ANSWER AND NOT A FAILURE. A driver whose only `profiles` row belongs to an admin has
 * no profile of their own, and that is exactly what the server says about them
 * (`DRIVER_PROFILE_INCOMPLETE`). Returning the admin's row instead — "something is better than
 * nothing" — is the defect this function exists to end: it puts a name on an agreement, and the
 * name would be wrong.
 *
 * DETERMINISTIC BY CONSTRUCTION. At most one row can survive the filter (the partial unique index
 * guarantees it), and the `id` sort is the belt to that braces: nothing about this pick may depend
 * on the order a row happened to arrive in.
 */
export function driverProfileOf<T extends DriverScopedProfile>(
    value: EmbeddedProfiles<T>,
): T | null {
    if (value == null) return null;
    const rows = (Array.isArray(value) ? value : [value]).filter(
        (row): row is T => row != null,
    );
    const owned = rows.filter((row) => (row.admin_id ?? null) === null);
    if (owned.length === 0) return null;
    if (owned.length === 1) return owned[0];
    return [...owned].sort((a, b) => (a.id ?? 0) - (b.id ?? 0))[0];
}

/**
 * `${first} ${last}` off the driver's own row, or null.
 *
 * The formatting is trivial; what it carries is the PICK, so a surface that only wants a name
 * never has to remember why `[0]` is wrong.
 */
export function driverProfileName<
    T extends DriverScopedProfile & { first_name?: string | null; last_name?: string | null },
>(value: EmbeddedProfiles<T>): string | null {
    const profile = driverProfileOf(value);
    if (!profile) return null;
    return [profile.first_name, profile.last_name].filter(Boolean).join(' ').trim() || null;
}

// ─── WHAT THE SERVER IS REFUSING OVER, SAID PRECISELY ────────────────────────

/**
 * The profile columns `get_contract_setup_status`'s driver arm grades, in the snapshot's vocabulary.
 *
 * FIVE COLUMNS, NOT FOUR. The server's sentence GROUPS the name — "Complete the driver's profile:
 * first and last name, date of birth, phone and address." — but its SQL tests five separate
 * predicates on the row `admin_id IS NULL` picks out:
 *
 *     pr.first_name IS NOT NULL AND pr.last_name IS NOT NULL AND pr.birth_date IS NOT NULL
 *       AND pr.phone IS NOT NULL AND pr.address IS NOT NULL
 *
 * Naming them one at a time is the SAME list said precisely, never a longer one: a driver missing
 * only a surname is refused by the server over `last_name` alone, and telling them "first and last
 * name" would send an admin back to a field that is already right.
 *
 * WEAK ON PURPOSE — every property optional, so a caller that hands over a wider record (the
 * page's `DriverFacts`) type-checks, while an object sharing none of these names is refused by
 * `tsc` rather than silently reading five `undefined`s as five empty fields.
 */
export interface DriverProfileFacts {
    firstName?: string | null;
    lastName?: string | null;
    birthDate?: string | null;
    phone?: string | null;
    address?: string | null;
}

/** Each graded column and the noun the sentence uses for it, in the server's own order. */
const PROFILE_FIELD_NOUNS: ReadonlyArray<readonly [keyof DriverProfileFacts, string]> = [
    ['firstName', 'the first name'],
    ['lastName', 'the last name'],
    ['birthDate', 'the date of birth'],
    ['phone', 'the phone number'],
    ['address', 'the address'],
];

/**
 * Which of the five the client can SEE as absent, in the server's order.
 *
 * `== null` AND NOTHING ELSE, BECAUSE THE SERVER'S TEST IS `IS NOT NULL`. An empty string is a
 * value to Postgres, so a profile whose address is `''` CLEARS the server's gate — and a client
 * that called it missing would name a field the activation is not actually held on. Mirroring the
 * predicate exactly is what keeps the two from ever describing different contracts; where they do
 * disagree, the caller falls back to the server's own sentence rather than to a guess of ours.
 */
export function missingDriverProfileFields(facts: DriverProfileFacts): string[] {
    return PROFILE_FIELD_NOUNS.filter(([key]) => facts[key] == null).map(([, noun]) => noun);
}

/** "A" · "A and B" · "A, B and C" — the server's own list style, with no serial comma. */
function conjoin(parts: readonly string[]): string {
    if (parts.length <= 1) return parts[0] ?? '';
    return `${parts.slice(0, -1).join(', ')} and ${parts[parts.length - 1]}`;
}

/**
 * `DRIVER_PROFILE_INCOMPLETE`, IN THE FIELDS THAT ARE ACTUALLY ABSENT — or null.
 *
 * THE COMPLAINT THIS EXISTS FOR: the server's message is a FIXED sentence naming all four groups,
 * so a contract held up by a missing date of birth alone told the admin to complete the name, the
 * phone and the address as well. The server sends the CODE and the client holds the row
 * (`driverProfileOf` — the same row, under the same `admin_id IS NULL` exclusion the SQL applies),
 * so the narrowing can only be done here.
 *
 * NULL MEANS "SAY WHAT THE SERVER SAID", AND IS THE ANSWER WHENEVER THE TWO DISAGREE. A client
 * that can see nothing missing under a live refusal is a client whose picture is STALE or whose
 * rules have drifted from the deployed function's — a re-read that has not landed, a column the
 * select forgot to ask for, an arm added to the server this file does not know. Answering
 * "everything is complete" there would contradict the only authority on the question, on the one
 * screen where an admin is trying to work out why activation is refused. So the failure direction
 * is fixed: name the gaps only when the client can actually see them, and otherwise repeat the
 * server's generic sentence verbatim.
 *
 * THE WORDS ARE THE CLIENT'S HERE, and typeset as the client's — `’`, as every other sentence this
 * app writes for a server code does (see `BLOCKER_COPY`). Verbatim binds what the server WROTE,
 * which is exactly what the null answer above preserves.
 */
export function driverProfileGapSentence(facts: DriverProfileFacts): string | null {
    const missing = missingDriverProfileFields(facts);
    if (missing.length === 0) return null;
    const verb = missing.length === 1 ? 'is' : 'are';
    return `Complete the driver’s profile: ${conjoin(missing)} ${verb} missing.`;
}
